import { useEffect, useRef, useState } from "react";
import useAddToCart from "api/BookResource/useAddToCart";
import useAddToPicks from "api/BookResource/useAddToPicks";
import useCheckIfUserIsSubscribed from "api/SubscriptionsResource/useCheckIfUserIsSubscribed";
import useFetchSubscriptionBenefits from "api/SubscriptionsResource/useFetchSubscriptionBenefits";
import useFreeBooksQuota from "api/BookResource/useFreeBooksQuota";
import useCheckBookAvailability from "api/BookResource/useCheckBookAvailability";
import useAllowedBookActions from "hooks/useAllowedBookActions";
import useAuthStore from "store/authStore";
import useCartStore from "store/cartStore";
import useCurrencyStore from "store/currencyStore";
import { priceInCurrency, formatCurrencyAmount } from "utils";
import { enabled, isInPersonalLibrary } from "./catalogueBookDetails";
import { picksBody } from "./subscriptionPicks";

export default function useCatalogueBookActions(book, { allowDigitalQuantity = false, onCartSuccess } = {}) {
	const loggedIn = useAuthStore((state) => state.loggedIn);
	const currency = useCurrencyStore((state) => state.currency);
	const clearCartDialog = useCartStore((state) => state.setAddToCartIsSuccessful);
	const subscription = useCheckIfUserIsSubscribed();
	const benefitsQuery = useFetchSubscriptionBenefits({ enabled: loggedIn });
	const benefits = benefitsQuery.isError ? [] : benefitsQuery.data?.data?.benefits || [];
	const quota = useFreeBooksQuota();
	const availability = useCheckBookAvailability(book.id, { enabled: enabled(book.geo_restricted) });
	const cart = useAddToCart(), picks = useAddToPicks();
	const [format, setFormat] = useState(book.format === "physical" ? "physical" : "digital");
	const [quantity, setQuantity] = useState(1);
	const [notice, setNotice] = useState(null);
	const [cartAdded, setCartAdded] = useState(false);
	const [picksAdded, setPicksAdded] = useState(false);
	const [pending, setPending] = useState(null);
	const requestPending = useRef(false);
	const mounted = useRef(true);
	useEffect(() => () => { mounted.current = false; }, []);
	const free = enabled(book.isFree);
	const allowed = useAllowedBookActions({ isFree: free, in_library: enabled(book.in_library), in_picks: enabled(book.in_picks) || picksAdded, is_purchased: enabled(book.is_purchased), sellable: enabled(book.sellable), subscribable: enabled(book.subscription), hasSubscription: Boolean(subscription.data?.data), freeBooksQuota: quota });
	const physical = format === "physical";
	const amount = physical ? priceInCurrency(book.physical_prices, currency, book.physical_usd_price, book.physical_zwl_price) : priceInCurrency(book.prices, currency, book.price, book.zwl_price);
	const hasPrice = amount != null && Number.isFinite(Number(amount)) && Number(amount) >= 0;
	const price = free ? "Free" : enabled(book.sellable) ? hasPrice ? formatCurrencyAmount(amount, currency) : "Price unavailable" : enabled(book.subscription) ? "Subscription" : "View access";
	const outOfStock = physical && book.stock_level != null && Number(book.stock_level) <= 0;
	const validQuantity = Number.isInteger(Number(quantity)) && Number(quantity) >= 1 && (!physical || book.stock_level == null || Number(quantity) <= Number(book.stock_level));
	const benefitsLoading = loggedIn && benefitsQuery.isLoading;
	const checking = loggedIn && (subscription.isLoading || quota.isLoading || benefitsLoading);
	const accessError = loggedIn && (subscription.isError || quota.isError || benefitsQuery.isError);
	const regionBlocked = enabled(book.geo_restricted) && availability.data?.data?.available === false;
	const regionLoading = enabled(book.geo_restricted) && (availability.isLoading || availability.isError);
	const owned = loggedIn && (isInPersonalLibrary(book) || picksAdded);
	const busy = cart.isLoading || picks.isLoading || Boolean(pending);
	const switchFormat = (next) => { setFormat(next); setQuantity(1); setCartAdded(false); setNotice(null); };
	const run = async (kind) => {
		if (busy || requestPending.current || regionBlocked || regionLoading) return;
		const permission = kind === "cart" ? allowed.addToCart : allowed.addToPicks;
		if (!permission.possible) { setNotice({ text: permission.message, action: permission.action }); return; }
		if (kind === "cart" && (outOfStock || !validQuantity || cartAdded)) return;
		if (kind === "picks" && (physical || checking || accessError)) return;
		requestPending.current = true;
		setPending(kind);
		setNotice(null);
		try {
			if (kind === "cart") { await cart.mutateAsync({ book_id: book.id, quantity: physical || allowDigitalQuantity ? Number(quantity) : 1, format }); clearCartDialog(false); onCartSuccess?.(); }
			else await picks.mutateAsync(picksBody(book.id, benefits));
			if (!mounted.current) return;
			if (kind === "cart") setCartAdded(true); else setPicksAdded(true);
			setNotice({ text: kind === "cart" ? "Added to your cart. Ready when you are." : "Added to your picks. Find it in your library.", action: { label: kind === "cart" ? "View cart" : "My library", link: kind === "cart" ? "/cart" : "/my-library" }, success: true });
		} catch (error) {
			if (mounted.current) setNotice({ text: error?.response?.data?.message || "That didn’t go through. Please try again.", error: true });
		} finally {
			requestPending.current = false;
			if (mounted.current) setPending(null);
		}
	};
	return { loggedIn, free, format, physical, switchFormat, quantity, setQuantity, price, owned, busy, checking, accessError, cartAdded, picksAdded, outOfStock, validQuantity, notice, run, allowed, resetPicks: () => { setPicksAdded(false); setNotice(null); }, regionBlocked, regionLoading, availability, cart: { ...cart, isLoading: cart.isLoading || pending === "cart" }, picks: { ...picks, isLoading: picks.isLoading || pending === "picks" }, retryAccess: () => { subscription.refetch(); quota.refetch(); benefitsQuery.refetch(); } };
}
